import { OgEvent, OgModule, OgProduct } from '@opengov/analytics';
import type { AnalyticsIdentity, ShellAnalyticsBridge } from '@opengov/analytics';
import {
  createAnalyticsBridge,
  createSharedAnalytics,
  getSharedAnalytics,
  hasSharedAnalytics,
  isAnalyticsConfigured,
  receiveModuleAnnouncements,
} from '@opengov/analytics/shell';

/**
 * PSP's analytics configuration.
 *
 * There is no key here, and no host, and no name of the variable either. They
 * live in `.env` and the SDK reads them — `ogAnalyticsEnv()` in `vite.config.ts`
 * injects them into the library at build time.
 *
 * So this file does not change when the analytics tool changes, when a delivery
 * host moves, or when a variable is renamed. What remains is the only part PSP
 * can know: that it is the shell, and that every one of its users is
 * authenticated.
 *
 * Two gates must both pass before a client exists: a key set for the current
 * mode, and the `psp-og-analytics` LaunchDarkly flag (see
 * `useOgAnalyticsEnabled`). Until then there is no client at all — no timers,
 * no listeners, no network. Every mode currently has a key, so the flag is
 * the switch that keeps an environment silent. A mode with no key stays
 * silent even with the flag on, and the plugin warns at build time so an
 * unset key is noticed.
 */

/** True once the client has been created — i.e. a key is configured and the
 *  flag has been seen on during this page load. Non-React callers (logout,
 *  shell bridge) read this; React code uses `useOgAnalyticsEnabled`. */
export function isOgAnalyticsEnabled(): boolean {
  return hasSharedAnalytics();
}

/** A sign-in that arrived before the client existed, waiting for creation. */
let signInPending = false;

/** Set when creating the client threw, so later renders do not retry (and
 *  re-report) on every pass. Analytics stays off for this page load. */
let initFailed = false;

type IdentityHandler = (identity: AnalyticsIdentity) => void;

/** The bridge over the shared client, created the first time it exists. */
let liveBridge: ShellAnalyticsBridge | undefined;

/** One entry per mounted module: attaches that module's held identity
 *  handlers. Run by `initOgAnalytics` once the client exists. */
const moduleScopes = new Set<() => void>();

/**
 * Idempotent. Called by `useOgAnalyticsEnabled` once the flag is on. Emits a
 * held sign-in and attaches held module identity subscriptions.
 *
 * Never throws. It runs during the render of `AppInitialization`, so an
 * exception here (posthog-js failing to start in a browser that blocks
 * storage, say) would reach the shell's error boundary and replace the whole
 * app. A failure is reported to Faro, and analytics stays off instead.
 */
export function initOgAnalytics(): void {
  if (initFailed || !isAnalyticsConfigured() || hasSharedAnalytics()) return;

  try {
    createClient();
  } catch (err) {
    initFailed = true;
    reportAnalyticsError(err, 'analytics_init_error');
    return;
  }

  if (signInPending) {
    signInPending = false;
    emitSignIn();
  }
  for (const attachHandlers of moduleScopes) {
    try {
      attachHandlers();
    } catch (err) {
      reportAnalyticsError(err, 'analytics_attach_error');
    }
  }
}

function createClient(): void {
  createSharedAnalytics({
    app: OgProduct.Psp,
    // PSP's own pages (landing, My Work, settings) carry `psp`; a route that
    // maps to an application (`/general-ledger/…` → `gl`) overrides it.
    module: OgModule.Psp,
    mode: 'standalone', // PSP *is* the shell, so it owns identity
    // Every PSP user is authenticated. Events fired before Auth0 and Entity
    // resolve are held, then stamped with identity and flushed — so the first
    // pageview still lands, attributed, rather than arriving anonymous.
    requireIdentity: true,
    // Browser instrumentation the SDK cannot produce itself: `$autocapture`,
    // `$rageclick`, `$pageleave`. All three come from this one option, with
    // element-text masking on by default.
    //
    // OFF in every environment until autocapture is approved for real users.
    // It reads element text; masking means structure rather than content
    // leaves the browser, but enabling it is a privacy decision with a masking
    // policy attached. Pageviews, identify and explicit `track()` calls are
    // unaffected.
    //
    // When approved, this one value is the switch. Autocapture binds
    // `document`, and PSP owns the document, so this instance covers mounted
    // modules too — including General Ledger at `/general-ledger/*`. A module
    // cannot switch it on for itself: in shell mode it has no destination
    // instance, and the provider's prop union refuses the option.
    passiveCapture: false,
    debug: import.meta.env.DEV,
  });
}

/**
 * Reports an analytics failure to Faro, like `useAuth.logoutUser` reports its
 * own. This code runs outside React, some of it before the logger context
 * exists, so it uses the `window.faro` global that Faro sets on start.
 */
function reportAnalyticsError(err: unknown, event: string): void {
  window.faro?.api.pushError(err instanceof Error ? err : new Error(String(err)), {
    context: { auth_event: event },
  });
}

/**
 * Records that a user signed in.
 *
 * Called from `Auth0Provider`'s `onRedirectCallback` in `main.tsx`, which
 * Auth0 invokes exactly once per completed login: after the `?code` exchange
 * succeeds, and never for a session restored from `localstorage`, a failed
 * exchange, or a callback on another app's route (`skipRedirectCallback`).
 * That makes it the event, rather than a state change to watch for.
 *
 * It fires before LaunchDarkly has answered, so the client usually does not
 * exist yet. The sign-in is then held and emitted by `initOgAnalytics` when
 * the flag turns the client on. If the flag never does, nothing is sent.
 *
 * The counterpart to the `signed_out` sequence in `useAuth.logoutUser`. Both
 * names come from `OgEvent` — the SDK's closed vocabulary — rather than being
 * spelled here, because a session event only earns its keep if PSP and every
 * module emit the same row. `signed_in` and `signed_out` are whole event names
 * in that vocabulary, not object+action pairs: there is no sign-in verb in
 * `OgAction`, deliberately, since a session has no object half.
 */
export function recordSignIn(): void {
  if (!isAnalyticsConfigured()) return;
  if (hasSharedAnalytics()) emitSignIn();
  else signInPending = true;
}

/** Never throws. This runs on the path a user takes into the product, and an
 *  analytics failure must not be able to interrupt it. */
function emitSignIn(): void {
  try {
    getSharedAnalytics().track(OgEvent.SignedIn, { method: 'auth0' });
  } catch (err) {
    reportAnalyticsError(err, 'sign_in_analytics_error');
  }
}

// ─── Module bridge ───────────────────────────────────────────────────

function getLiveBridge(): ShellAnalyticsBridge | undefined {
  if (!liveBridge && hasSharedAnalytics()) liveBridge = createAnalyticsBridge(getSharedAnalytics());
  return liveBridge;
}

/** A module's `shell.analytics`, and the dispose that retires it on unmount. */
export interface ModuleAnalytics {
  bridge: ShellAnalyticsBridge;
  dispose: () => void;
}

/**
 * Creates the `shell.analytics` for one mounted module, or returns `undefined`
 * when this build has no key. Then there is never a client, so modules
 * feature-detect instead.
 *
 * The bridge is created at mount and never replaced, and it forwards to the
 * shared client once that exists. It can't be swapped in later, because a
 * module's SDK client captures `shell.analytics` once, when it starts, and
 * treats its presence as "I am in a shell". A module that mounts before
 * `psp-og-analytics` resolves (a global module mounts on the first render)
 * would otherwise keep `undefined` for its whole life.
 *
 * Until the client exists, `track`/`page` are dropped (the shell's own route
 * tracking still records the pageview once it starts), `getIdentity` returns
 * an empty identity, and `onIdentityChange` handlers are held and attached on
 * creation.
 *
 * `dispose` makes the bridge inert and unsubscribes its handlers, so an
 * unmounted module can neither keep sending events under the signed-in
 * identity nor keep reading it. It also clears the module's announcements
 * from the shell's banner slot.
 */
export function createModuleAnalytics(): ModuleAnalytics | undefined {
  if (!isAnalyticsConfigured()) return undefined;

  let disposed = false;
  /** Each handler, mapped to its unsubscribe once attached to the live bridge. */
  const handlers = new Map<IdentityHandler, (() => void) | undefined>();

  const attachHandlers = () => {
    const live = getLiveBridge();
    if (!live) return;
    for (const [handler, unsubscribe] of handlers) {
      if (!unsubscribe) handlers.set(handler, live.onIdentityChange(handler));
    }
  };
  moduleScopes.add(attachHandlers);

  /** Every announcement owner this module has written, so `dispose` can clear
   *  exactly those from the shell's banner slot. */
  const announcementOwners = new Set<string>();

  const bridge: ShellAnalyticsBridge = {
    // Not routed through the client: announcements show whether or not
    // `psp-og-analytics` has created one. `<PspAnnouncements />` reads the
    // list, re-validated, from the SDK's hub.
    setAnnouncements(owner, list) {
      if (disposed) return;
      announcementOwners.add(owner);
      receiveModuleAnnouncements(owner, list);
    },
    track(event, properties) {
      if (!disposed) getLiveBridge()?.track(event, properties);
    },
    page(name, properties) {
      if (!disposed) getLiveBridge()?.page(name, properties);
    },
    getIdentity: () => (disposed ? {} : (getLiveBridge()?.getIdentity() ?? {})),
    onIdentityChange(handler) {
      if (disposed) return () => {};
      // A repeat registration of the same handler keeps its first binding.
      // Resetting it would orphan that binding on the live bridge, where
      // `dispose` could no longer reach it.
      if (!handlers.has(handler)) handlers.set(handler, undefined);
      attachHandlers();
      return () => {
        handlers.get(handler)?.();
        handlers.delete(handler);
      };
    },
  };

  const dispose = () => {
    disposed = true;
    // Identity first: nothing below may leave an unmounted module subscribed.
    moduleScopes.delete(attachHandlers);
    for (const unsubscribe of handlers.values()) unsubscribe?.();
    handlers.clear();
    // A module's banners show only while it is mounted, even if it never got
    // to send its own `[]` on unmount. One owner failing to clear must not
    // keep the others on screen.
    for (const owner of announcementOwners) {
      try {
        receiveModuleAnnouncements(owner, []);
      } catch (err) {
        reportAnalyticsError(err, 'announcements_clear_error');
      }
    }
    announcementOwners.clear();
  };

  return { bridge, dispose };
}

export { getSharedAnalytics as getOgAnalytics };
